import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { CheckIcon, RotateCcwIcon, SparklesIcon, Undo2Icon, WandSparklesIcon } from "lucide-react";
import { PromptRewritePreview } from "./PromptRewritePreview";
import {
  beginPromptReview,
  closePromptReview,
  finishPromptReview,
  type PromptAssistanceState,
} from "./promptAssistanceReview";
import { Button } from "../ui/button";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { Popover, PopoverPopup, PopoverTitle, PopoverDescription } from "../ui/popover";
import { cn } from "../../lib/utils";
import {
  PromptAssistanceAnnotation,
  createPromptAssistanceAnchor,
  createPromptAnchorStabilityCheck,
  isOverPromptText,
  PROMPT_ASSISTANCE_CONTROL_SELECTOR,
} from "./promptAssistanceAnnotation";
import { composerFloatingLayerProps } from "./composerEventScope";
import {
  canAssessPrompt,
  PROMPT_ASSESSMENT_DELAY_MS,
  requestPromptAssistance,
  streamPromptEnhancement,
} from "../../state/promptAssistance";

export function ComposerPromptAssistance(props: {
  prompt: string;
  context: string;
  organizationId: string | null;
  enabled: boolean;
  onApply: (prompt: string) => void;
  onFocusComposer: () => void;
  /** Footer slot beside the attach button for the enhance and undo actions. */
  actionHost: HTMLElement | null;
  surfaceRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}) {
  const key = JSON.stringify([props.organizationId, props.prompt, props.context, props.enabled]);
  const draftKey = JSON.stringify([props.organizationId, props.prompt]);
  const latest = useRef({ ...props, key });
  latest.current = { ...props, key };
  const [state, setState] = useState<PromptAssistanceState | null>(null);
  const [dismissedDraftKey, setDismissedDraftKey] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [popupAnchor, setPopupAnchor] = useState<ReturnType<
    typeof createPromptAssistanceAnchor
  > | null>(null);
  const controller = useRef<AbortController | null>(null);
  const assessments = useRef(new Map<string, boolean>());
  const editorRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const enhanceButtonRef = useRef<HTMLButtonElement>(null);
  const focusOnOpen = useRef(false);
  const descriptionId = useId();
  const visible = props.enabled && state?.key === key ? state : null;
  const active = !!visible && visible.phase !== "applied";
  const open = active && openKey === key;
  const expanded =
    visible?.phase === "enhancing" || visible?.phase === "preview" || visible?.phase === "error";
  const popupWidth = expanded
    ? "w-[min(32rem,calc(100vw-2rem))]"
    : "w-[min(21rem,calc(100vw-2rem))]";

  useEffect(() => {
    // Context updates keep a dismissal, but a later draft may reuse the same words.
    setDismissedDraftKey((current) => (current === draftKey ? current : null));
  }, [draftKey]);

  useLayoutEffect(() => {
    if (!open || visible?.phase !== "enhancing") return;
    const focused = document.activeElement;
    if (focused === document.body || (focused && popupRef.current?.contains(focused))) {
      previewRef.current?.focus({ preventScroll: true });
    }
  }, [open, visible?.phase]);

  const closeCard = useCallback(() => {
    // A portaled popup may report focus-out after a draft edit has already started
    // a new assessment. Its closing event belongs only to the previous card.
    if (latest.current.key !== key || openKey !== key) return;
    controller.current?.abort();
    setOpenKey(null);
    setState((current) => (current?.key === key ? closePromptReview(current) : current));
  }, [key, openKey]);
  const openCard = useCallback(
    (keyboard = false, anchor?: ReturnType<typeof createPromptAssistanceAnchor>) => {
      if (!active || latest.current.key !== key) return;
      focusOnOpen.current = keyboard;
      const root = editorRef.current;
      setPopupAnchor(anchor ?? (root ? createPromptAssistanceAnchor(root) : null));
      setOpenKey(key);
    },
    [active, key],
  );

  useEffect(() => {
    const root = editorRef.current;
    const surface = props.surfaceRef.current;
    if (!active || !root || !surface) return;
    const ownerDocument = root.ownerDocument;
    let pressedAnchor: ReturnType<typeof createPromptAssistanceAnchor> | null = null;
    let frame: number | null = null;
    const cancelFrame = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };
    const capturePress = (event: PointerEvent) => {
      cancelFrame();
      const hitText =
        event.button === 0 &&
        event.target instanceof Element &&
        root.contains(event.target) &&
        !event.target.closest(PROMPT_ASSISTANCE_CONTROL_SELECTOR) &&
        isOverPromptText(root, event.clientX, event.clientY);
      pressedAnchor = hitText
        ? createPromptAssistanceAnchor(root, { x: event.clientX, y: event.clientY })
        : null;
    };
    const reviewClick = (event: MouseEvent) => {
      const anchor = pressedAnchor;
      pressedAnchor = null;
      if (
        !anchor ||
        event.button !== 0 ||
        !(event.target instanceof Element) ||
        event.target.closest(PROMPT_ASSISTANCE_CONTROL_SELECTOR)
      )
        return;
      // Focusing the resting composer animates its text upward. Mount the card at
      // the settled anchor instead of showing it at an intermediate measurement.
      // A subsequent press or focus change cancels opening; selection stays native.
      const isStable = createPromptAnchorStabilityCheck();
      const place = (at: number) => {
        frame = null;
        if (!root.contains(ownerDocument.activeElement)) return;
        if (!ownerDocument.getSelection()?.isCollapsed) {
          if (!expanded) closeCard();
          return;
        }
        if (isStable(anchor.getBoundingClientRect(), at)) openCard(false, anchor);
        else frame = requestAnimationFrame(place);
      };
      frame = requestAnimationFrame(place);
    };
    // Measure before React's focus handlers expand the composer. Its text can move
    // beneath the pointer, so the resulting click belongs to the stable surface.
    ownerDocument.addEventListener("pointerdown", capturePress, true);
    surface.addEventListener("click", reviewClick);
    return () => {
      cancelFrame();
      ownerDocument.removeEventListener("pointerdown", capturePress, true);
      surface.removeEventListener("click", reviewClick);
    };
  }, [active, expanded, openCard, closeCard, props.surfaceRef]);

  useEffect(() => {
    controller.current?.abort();
    setOpenKey(null);
    setState((current) => (current?.phase === "applied" && current.key === key ? current : null));
    if (
      !props.enabled ||
      !props.organizationId ||
      !canAssessPrompt(props.prompt) ||
      dismissedDraftKey === draftKey
    )
      return;
    const cached = assessments.current.get(key);
    if (cached !== undefined) {
      if (cached) setState({ key, phase: "suggested" });
      return;
    }
    const abort = new AbortController();
    controller.current = abort;
    const timer = setTimeout(() => {
      void requestPromptAssistance(
        {
          organizationId: props.organizationId!,
          action: "assess",
          prompt: props.prompt,
          context: props.context,
        },
        abort.signal,
      )
        .then((result) => {
          if (abort.signal.aborted || latest.current.key !== key || result.action !== "assess")
            return;
          if (assessments.current.size >= 12) assessments.current.clear();
          assessments.current.set(key, result.needsEnhancement);
          if (result.needsEnhancement) setState({ key, phase: "suggested" });
        })
        .catch(() => {}); // Sending never depends on the quiet background assessment.
    }, PROMPT_ASSESSMENT_DELAY_MS);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [
    key,
    draftKey,
    dismissedDraftKey,
    props.context,
    props.enabled,
    props.organizationId,
    props.prompt,
  ]);

  useEffect(
    () => () => {
      controller.current?.abort();
    },
    [],
  );

  function dismiss() {
    if (latest.current.key !== key) return;
    closeCard();
    setDismissedDraftKey(draftKey);
    setState(null);
    props.onFocusComposer();
  }
  async function enhance(requested = false) {
    if (!props.organizationId || latest.current.key !== key || (!active && !requested)) return;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setState((current) => beginPromptReview(key, current));
    try {
      const prompt = await streamPromptEnhancement(
        {
          organizationId: props.organizationId,
          action: "enhance",
          prompt: props.prompt,
          context: props.context,
          ...(visible && "completedPrompt" in visible && visible.completedPrompt
            ? { previousPrompt: visible.completedPrompt }
            : {}),
        },
        abort.signal,
        (text) => {
          if (!abort.signal.aborted && latest.current.key === key)
            setState((current) =>
              current?.key === key && current.phase === "enhancing"
                ? { ...current, prompt: text }
                : current,
            );
        },
      );
      if (!abort.signal.aborted && latest.current.key === key)
        setState((current) =>
          current?.key === key ? finishPromptReview(current, prompt) : current,
        );
    } catch {
      if (!abort.signal.aborted && latest.current.key === key)
        setState((current) =>
          current?.key === key && current.phase === "enhancing"
            ? { ...current, phase: "error", prompt: "" }
            : current,
        );
    }
  }
  /** The footer button rewrites any draft, whether or not assessment flagged it. */
  function enhanceOnRequest() {
    if (!canEnhanceOnRequest || latest.current.key !== key) return;
    // A second press closes the card; a finished rewrite stays to reopen.
    if (open) return closeCard();
    const surface = props.surfaceRef.current;
    focusOnOpen.current = false;
    // Opened from the footer, the card centres over the composer, not the caret.
    setPopupAnchor(
      surface
        ? {
            contextElement: surface,
            getBoundingClientRect: () => {
              const bounds = surface.getBoundingClientRect();
              return DOMRect.fromRect({ x: bounds.left + bounds.width / 2, y: bounds.top });
            },
          }
        : null,
    );
    setOpenKey(key);
    // An open rewrite, streaming or complete, is reopened rather than replaced.
    if (!visible || visible.phase === "suggested" || visible.phase === "error") void enhance(true);
  }
  const canEnhanceOnRequest =
    props.enabled && !!props.organizationId && canAssessPrompt(props.prompt);
  const textAction =
    "min-h-8 rounded-sm px-1 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring motion-reduce:transition-none";
  return (
    <PromptAssistanceAnnotation
      value={{
        active,
        editorRef,
        descriptionId,
        onKeyDown: (event) => {
          if (
            active &&
            event.altKey &&
            event.key === "Enter" &&
            !event.nativeEvent.isComposing &&
            event.nativeEvent.keyCode !== 229
          ) {
            event.preventDefault();
            event.stopPropagation();
            openCard(true);
          }
        },
      }}
    >
      {props.children}
      {active ? (
        <>
          <span id={descriptionId} className="sr-only">
            This prompt could use more detail. Click the underlined text or press Alt+Enter to
            review a suggestion.
          </span>
          <button
            type="button"
            className="sr-only focus:not-sr-only focus:mt-2 focus:rounded focus:px-2 focus:py-1 focus:text-xs focus:outline-ring"
            onClick={() => openCard(true)}
          >
            Review prompt suggestion
          </button>
        </>
      ) : null}
      {props.actionHost && canEnhanceOnRequest && visible?.phase !== "applied"
        ? createPortal(
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    // Slides out from under the attach button and sits close beside it.
                    className="-mr-1.5 animate-in fade-in slide-in-from-right-2 duration-200 motion-reduce:animate-none"
                    ref={enhanceButtonRef}
                    data-testid="prompt-assistance-enhance"
                    aria-label="Enhance prompt"
                    aria-expanded={open}
                    data-pressed={open ? "" : undefined}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={enhanceOnRequest}
                  />
                }
              >
                <WandSparklesIcon aria-hidden="true" className="size-3.5 stroke-[1.75]" />
              </TooltipTrigger>
              <TooltipPopup>Enhance prompt</TooltipPopup>
            </Tooltip>,
            props.actionHost,
          )
        : null}
      {visible?.phase === "applied" && props.actionHost
        ? createPortal(
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    // Takes the wand's place without shifting the footer.
                    className="-mr-1.5"
                    data-testid="prompt-assistance-undo"
                    aria-label="Undo"
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => {
                      if (latest.current.key !== visible.key) return;
                      setDismissedDraftKey(visible.originalDraftKey);
                      setState(null);
                      props.onApply(visible.originalPrompt);
                    }}
                  />
                }
              >
                <Undo2Icon aria-hidden="true" className="size-3.5 stroke-[1.75]" />
              </TooltipTrigger>
              <TooltipPopup>Restore original prompt</TooltipPopup>
            </Tooltip>,
            props.actionHost,
          )
        : null}
      <Popover
        open={open}
        modal={false}
        onOpenChange={(next, details) => {
          if (next) return;
          // The wand toggles the card itself; its press is not a dismissal.
          if (
            details.reason === "outside-press" &&
            enhanceButtonRef.current?.contains(details.event.target as Node)
          )
            return;
          // Opening review keeps focus in the editor. Moving the caret is still editing.
          if (
            details.reason === "focus-out" &&
            editorRef.current?.contains(details.event.target as Node)
          )
            return;
          closeCard();
          if (details.reason === "escape-key") props.onFocusComposer();
        }}
      >
        <PopoverPopup
          {...composerFloatingLayerProps}
          ref={popupRef}
          anchor={popupAnchor ?? editorRef}
          side="top"
          align="center"
          sideOffset={12}
          positionerClassName={cn(popupWidth, "transition-none")}
          initialFocus={() => focusOnOpen.current}
          finalFocus={false}
          data-testid="prompt-assistance"
          aria-label={expanded ? "Enhanced prompt preview" : "Prompt suggestion"}
          className={cn(
            "prompt-assistance-popup h-auto border border-border/70 transition-opacity data-starting-style:scale-100 motion-reduce:transition-none",
            popupWidth,
          )}
          viewportClassName="h-auto max-h-[min(var(--available-height),calc(100dvh-2rem))] overflow-hidden p-0 not-data-transitioning:overflow-hidden [--viewport-inline-padding:0px] [&>[data-current]]:flex [&>[data-current]]:max-h-[inherit] [&>[data-current]]:flex-col"
        >
          <div className="shrink-0 px-4 pt-3.5">
            <PopoverTitle className="flex items-center gap-2 text-[13px] font-medium leading-5">
              <SparklesIcon className="size-3.5 text-primary" aria-hidden="true" />
              {expanded ? "A fuller prompt" : "Add a little more direction"}
            </PopoverTitle>
            <PopoverDescription className={expanded ? "sr-only" : "mt-1.5 text-xs leading-relaxed"}>
              {expanded
                ? "Review the rewrite before replacing your draft."
                : "A clearer goal or target could help the assistant get started."}
            </PopoverDescription>
          </div>
          {visible && "completedPrompt" in visible ? (
            <PromptRewritePreview
              review={visible}
              originalPrompt={props.prompt}
              previewRef={previewRef}
              onRestore={() => {
                if (latest.current.key !== key) return;
                controller.current?.abort();
                setState((current) =>
                  current?.key === key ? closePromptReview(current) : current,
                );
                previewRef.current?.focus({ preventScroll: true });
              }}
            />
          ) : null}
          <div
            className={cn(
              "flex shrink-0 items-center gap-3 px-4 py-3",
              expanded && "border-t border-border/60",
            )}
          >
            <button type="button" className={textAction} onClick={dismiss}>
              {expanded ? "Cancel" : "Keep"}
            </button>
            {expanded ? (
              <>
                <button
                  type="button"
                  className={`${textAction} ml-auto inline-flex items-center gap-1.5`}
                  onClick={() => void enhance()}
                >
                  <RotateCcwIcon className="size-3" aria-hidden="true" />
                  {visible?.phase === "error" ? "Try again" : "Redo"}
                </button>
                <Button
                  type="button"
                  size="sm"
                  disabled={visible?.phase !== "preview"}
                  onClick={() => {
                    if (visible?.phase !== "preview" || latest.current.key !== visible.key) return;
                    const acceptedKey = JSON.stringify([
                      props.organizationId,
                      visible.prompt,
                      props.context,
                      props.enabled,
                    ]);
                    assessments.current.set(acceptedKey, false);
                    setOpenKey(null);
                    setState({
                      key: acceptedKey,
                      phase: "applied",
                      originalDraftKey: draftKey,
                      originalPrompt: props.prompt,
                    });
                    props.onApply(visible.prompt);
                  }}
                >
                  <CheckIcon className="size-3.5" aria-hidden="true" />
                  Use rewrite
                </Button>
              </>
            ) : (
              <Button type="button" size="sm" className="ml-auto" onClick={() => void enhance()}>
                Enhance prompt <SparklesIcon className="size-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
        </PopoverPopup>
      </Popover>
    </PromptAssistanceAnnotation>
  );
}
